// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * Renders a monitoring surface only for someone who should be using it.
 *
 * Says who to ask rather than only that the door is shut. "Not authorized" with
 * no route forward turns into a support ticket; naming the reason and the
 * person who can change it turns into a conversation with the practice
 * administrator, which is where the decision actually belongs.
 *
 * This is navigation scoping and not a security boundary — see
 * `utils/monitoringAccess.ts`. It is the right place for the message, and the
 * wrong place to rely on for protecting data.
 */

import { Box, Loader, Stack, Text } from '@mantine/core';
import type { JSX, ReactNode } from 'react';
import { useMonitoringAccess } from '../../hooks/useMonitoringAccess';

export function ModuleGate({
  module,
  children,
}: {
  readonly module: 'rpm' | 'command-center';
  readonly children: ReactNode;
}): JSX.Element {
  const access = useMonitoringAccess();

  if (access.loading) {
    /* Not a denial: see the note on useMonitoringAccess about flicker. */
    return (
      <Box p="xl">
        <Loader size="sm" />
      </Box>
    );
  }

  const allowed = module === 'rpm' ? access.rpm : access.commandCenter;
  if (allowed) {
    return <>{children}</>;
  }

  const label = module === 'rpm' ? 'the monitoring module' : 'the RPM Command Center';

  return (
    <Box p="xl">
      <Stack gap={8} style={{ maxWidth: 560 }}>
        <Text
          size="xs"
          tt="uppercase"
          fw={600}
          style={{ letterSpacing: '0.12em', color: 'var(--text-muted)' }}
        >
          Not available to you
        </Text>
        <Text size="sm" style={{ color: 'var(--text-body)', lineHeight: 1.6 }}>
          Your account does not have access to {label} at this practice.
        </Text>
        <Text size="xs" style={{ color: 'var(--text-muted)', lineHeight: 1.6 }}>
          {access.reason}
        </Text>
        <Text size="xs" style={{ color: 'var(--text-muted)', lineHeight: 1.6 }}>
          A practice administrator can change this on your role assignment. If the practice has
          not purchased monitoring, that is a billing change rather than a permission one.
        </Text>
      </Stack>
    </Box>
  );
}
